"use client";

import { useEffect, useRef, useState } from "react";
import { useClerk } from "@clerk/nextjs";
import { Loader2, X } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  NATIVE_APPLE_CREDENTIAL_EVENT,
  parseNativeAppleCredential,
  requestNativeAppleSignInTicket,
  type NativeAppleCredential,
  type NativeAppleSignUpNeed,
} from "@award-party/shared";
import { NativeAppleSignUpForm } from "@/components/native-apple-sign-up-form";
import { getNativePartyPassCheckoutContext } from "@/lib/native-checkout-bridge";
import { capturePostHogEvent } from "@/lib/posthog-capture";
import {
  NATIVE_APPLE_SHEET_TRIGGER_URL,
  appleIdentityTokenDiagnostics,
  findClerkAppleButton,
  nativeAppleRedirectPath,
  redeemAppleSignInTicket,
  withRememberedAppleName,
} from "@/lib/native-apple-sign-in";

export const NATIVE_APPLE_SIGN_IN_FAILED_KEY = "ap:native-apple-sign-in-failed";

type LoadedClerk = ReturnType<typeof useClerk>;
type AppleSuccess = Extract<NativeAppleCredential, { status: "success" }>;

interface SignInHandlers {
  fail(code: string): void;
  setPending(pending: boolean): void;
  needDetails(needs: NativeAppleSignUpNeed[], pending: string): void;
}

interface OpenSignUp {
  needs: NativeAppleSignUpNeed[];
  pending: string;
  openedHere: boolean;
}

function readAndClearFailure(): string | null {
  try {
    const code = sessionStorage.getItem(NATIVE_APPLE_SIGN_IN_FAILED_KEY);
    sessionStorage.removeItem(NATIVE_APPLE_SIGN_IN_FAILED_KEY);
    return code;
  } catch {
    return null;
  }
}

function reportFailure(code: string) {
  capturePostHogEvent("native_apple_sign_in_failed", { code });
}

/**
 * When Clerk's own Apple flow opened the sheet, its button is left spinning,
 * so the page reloads. The code survives the reload in sessionStorage and is
 * reported afterwards, since an event sent right before a reload is lost.
 */
function reloadAfterFailure(code: string) {
  try {
    sessionStorage.setItem(NATIVE_APPLE_SIGN_IN_FAILED_KEY, code);
  } catch {
    // The reload still resets the page without the message.
  }
  window.location.reload();
}

function localStorageOrNull(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

async function redeemTicket(clerk: LoadedClerk, ticket: string, handlers: SignInHandlers) {
  const client = clerk.client;
  if (!clerk.loaded || !client) {
    handlers.fail("clerk_not_loaded");
    return;
  }
  handlers.setPending(true);
  const result = await redeemAppleSignInTicket(
    (params) => client.signIn.create(params),
    ticket,
  );
  if (result.kind === "failed") {
    handlers.fail(result.code);
    return;
  }
  if (result.kind === "complete") {
    try {
      await clerk.setActive({ session: result.sessionId });
    } catch (error) {
      console.error("[native-apple-sign-in] could not activate the session", error);
      handlers.fail("set_active_failed");
      return;
    }
  }
  window.location.replace(
    nativeAppleRedirectPath(window.location.search, window.location.origin),
  );
}

async function completeNativeAppleSignIn(
  clerk: LoadedClerk,
  credential: AppleSuccess,
  handlers: SignInHandlers,
) {
  if (clerk.user) {
    window.location.replace(
      nativeAppleRedirectPath(window.location.search, window.location.origin),
    );
    return;
  }
  if (!clerk.loaded || !clerk.client) {
    handlers.fail("clerk_not_loaded");
    return;
  }

  handlers.setPending(true);
  const result = await requestNativeAppleSignInTicket(
    (input, init) => fetch(input, init),
    withRememberedAppleName(credential, localStorageOrNull()),
  );
  if (result.status === "failed") {
    handlers.fail(result.code);
    return;
  }
  if (result.status === "needs") {
    handlers.needDetails(result.needs, result.pending);
    return;
  }
  await redeemTicket(clerk, result.ticket, handlers);
}

function signInHandlers(
  openedHere: boolean,
  setPending: (pending: boolean) => void,
  setFailureCode: (code: string) => void,
  setOpenSignUp: (signUp: OpenSignUp) => void,
): SignInHandlers {
  return {
    fail(code) {
      console.error("[native-apple-sign-in] Apple sign-in failed", code);
      if (!openedHere) {
        reloadAfterFailure(code);
        return;
      }
      reportFailure(code);
      setPending(false);
      setFailureCode(code);
    },
    setPending,
    needDetails(needs, pendingSignUp) {
      capturePostHogEvent("native_apple_sign_up_details_requested", { needs: needs.join(",") });
      setPending(false);
      setOpenSignUp({ needs, pending: pendingSignUp, openedHere });
    },
  };
}

export function NativeAppleSignInBridge() {
  const clerk = useClerk();
  const t = useTranslations("Auth");
  const [failureCode, setFailureCode] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [openSignUp, setOpenSignUp] = useState<OpenSignUp | null>(null);
  const openedByBridge = useRef(false);

  useEffect(() => {
    const code = readAndClearFailure();
    if (code) {
      reportFailure(code);
      // Not cancelled on cleanup: Strict Mode's second mount no longer sees the code.
      queueMicrotask(() => setFailureCode(code));
    }
  }, []);

  // Clerk runs a Turnstile captcha before an Apple sign-up can even redirect,
  // and it never finishes in the app's WebView. Taking the click first opens
  // the native sheet straight away and keeps Clerk's button from spinning.
  useEffect(() => {
    if (getNativePartyPassCheckoutContext()?.platform !== "ios") {
      return;
    }
    const onClick = (event: MouseEvent) => {
      if (!findClerkAppleButton(event.target)) {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      openedByBridge.current = true;
      setFailureCode(null);
      window.location.assign(NATIVE_APPLE_SHEET_TRIGGER_URL);
    };
    window.addEventListener("click", onClick, true);
    return () => window.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => {
    let inFlight = false;
    const onCredential = (event: Event) => {
      const credential = parseNativeAppleCredential((event as CustomEvent<unknown>).detail);
      if (!credential || inFlight) {
        return;
      }
      const openedHere = openedByBridge.current;
      openedByBridge.current = false;
      const handlers = signInHandlers(openedHere, setPending, setFailureCode, setOpenSignUp);

      if (credential.status === "canceled") {
        if (!openedHere) {
          window.location.reload();
        }
        return;
      }
      if (credential.status === "error") {
        handlers.fail("native_error");
        return;
      }
      capturePostHogEvent("native_apple_credential_received", {
        ...appleIdentityTokenDiagnostics(credential.identityToken),
        has_first_name: credential.firstName !== null,
        has_last_name: credential.lastName !== null,
      });
      inFlight = true;
      void completeNativeAppleSignIn(clerk, credential, handlers)
        .catch((error: unknown) => {
          console.error("[native-apple-sign-in] unexpected failure", error);
          handlers.fail("unexpected");
        })
        .finally(() => {
          inFlight = false;
        });
    };
    window.addEventListener(NATIVE_APPLE_CREDENTIAL_EVENT, onCredential);
    return () => window.removeEventListener(NATIVE_APPLE_CREDENTIAL_EVENT, onCredential);
  }, [clerk]);

  if (openSignUp) {
    const handlers = signInHandlers(
      openSignUp.openedHere,
      setPending,
      setFailureCode,
      setOpenSignUp,
    );
    return (
      <NativeAppleSignUpForm
        needs={openSignUp.needs}
        pending={openSignUp.pending}
        onTicket={(ticket) => {
          setOpenSignUp(null);
          void redeemTicket(clerk, ticket, handlers).catch((error: unknown) => {
            console.error("[native-apple-sign-in] unexpected failure", error);
            handlers.fail("unexpected");
          });
        }}
        onFailure={reportFailure}
        onCancel={() => {
          setOpenSignUp(null);
          if (!openSignUp.openedHere) {
            window.location.reload();
          }
        }}
      />
    );
  }

  if (pending) {
    return (
      <div className="pointer-events-none fixed inset-x-0 top-[calc(1rem+max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px)))] z-[10050] flex justify-center px-4">
        <div
          role="status"
          className="flex items-center gap-3 rounded-xl border border-(--mk-cream-08) bg-(--mk-ink-soft) px-4 py-3 shadow-[0_12px_40px_rgba(0,0,0,0.5)]"
        >
          <Loader2 className="h-4 w-4 animate-spin text-(--mk-gold)" aria-hidden="true" />
          <p className="text-sm text-(--mk-cream-75)">{t("appleSignInProgress")}</p>
        </div>
      </div>
    );
  }

  if (!failureCode) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 top-[calc(1rem+max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px)))] z-[10050] flex justify-center px-4">
      <div
        role="alert"
        className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-[rgba(255,100,80,0.3)] bg-[#1e1512] px-4 py-3 shadow-[0_12px_40px_rgba(0,0,0,0.5)]"
      >
        <p className="flex-1 text-sm leading-normal text-[#FF9082]">
          {t("appleSignInFailed")}
        </p>
        <button
          type="button"
          onClick={() => setFailureCode(null)}
          aria-label={t("appleSignInDismiss")}
          className="-mr-1 cursor-pointer rounded-md p-1 text-(--mk-cream-40) transition-colors hover:text-(--mk-cream-75)"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
